import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/Card";
import { PageHeader } from "@/components/PageHeader";
import { fetchCurrentUser } from "@/api/auth";
import { ProfilSection } from "./settings/ProfilSection";
import { VorlagenSection } from "./settings/VorlagenSection";
import { PdfVorlagenSection } from "./settings/PdfVorlagenSection";
import { MailSection } from "./settings/MailSection";
import { PreiseSection } from "./settings/PreiseSection";
import { DatenSection } from "./settings/DatenSection";
import { NutzerSection } from "./settings/NutzerSection";
import { AboutSection } from "./settings/AboutSection";
import { SystemSection } from "./settings/SystemSection";
import { FormulareSection } from "./settings/FormulareSection";
import { CiDesignSection } from "./settings/CiDesignSection";
import { HandbuchSection } from "./settings/HandbuchSection";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

type SectionId = "profil" | "vorlagen" | "pdf-vorlagen" | "mail" | "ci-design" | "formulare" | "preise" | "daten" | "nutzer" | "about" | "system" | "handbuch";

type NavItem = { id: SectionId; icon: string; label: string };
type NavGroup = { group: string; items: NavItem[] };

const SECTIONS: NavGroup[] = [
  {
    group: "Konto",
    items: [{ id: "profil", icon: "person", label: "Profil" }],
  },
  {
    group: "Kommunikation",
    items: [
      { id: "vorlagen", icon: "description", label: "E-Mail-Vorlagen" },
      { id: "pdf-vorlagen", icon: "picture_as_pdf", label: "PDF-Vorlagen" },
      { id: "mail", icon: "send", label: "Mail-Einstellungen" },
      { id: "ci-design", icon: "palette", label: "E-Mail-Design" },
      { id: "formulare", icon: "web_asset", label: "Formulare" },
    ],
  },
  {
    group: "Haus",
    items: [{ id: "preise", icon: "euro_symbol", label: "Preise & Saisons" }],
  },
  {
    group: "System",
    items: [
      { id: "system", icon: "tune", label: "System-Einstellungen" },
      { id: "nutzer", icon: "group", label: "Nutzerverwaltung" },
      { id: "daten", icon: "storage", label: "Daten & Export" },
      { id: "handbuch", icon: "menu_book", label: "Handbuch" },
      { id: "about", icon: "info", label: "Über" },
    ],
  },
];

const SECTION_IDS = SECTIONS.flatMap((g) => g.items.map((i) => i.id));

/** Flache, durchsuchbare Liste der Einstellungs-Bereiche für die Command Palette. */
export const SETTINGS_SEARCH_ITEMS: { id: SectionId; label: string; group: string; icon: string; path: string }[] =
  SECTIONS.flatMap((g) =>
    g.items.map((i) => ({ id: i.id, label: i.label, group: g.group, icon: i.icon, path: `/settings?section=${i.id}` })),
  );

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramSection = searchParams.get("section") as SectionId | null;
  const section: SectionId = paramSection && SECTION_IDS.includes(paramSection) ? paramSection : "profil";
  const setSection = (id: SectionId) => setSearchParams({ section: id }, { replace: true });
  const meQuery = useQuery({ queryKey: ["me"], queryFn: fetchCurrentUser });
  const isAdmin = (meQuery.data?.role || "").toLowerCase() === "admin";

  const activeLabel = SECTIONS.flatMap((g) => g.items).find((i) => i.id === section)?.label;
  useDocumentTitle(activeLabel ? `Einstellungen · ${activeLabel}` : "Einstellungen");

  return (
    <div>
      <PageHeader title="Einstellungen" subtitle="Konfiguration des Buchungssystems" />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "240px 1fr",
          gap: 20,
          alignItems: "start",
        }}
      >
        <Card padded={false} style={{ position: "sticky", top: 76 }}>
          <div style={{ padding: "10px 8px" }}>
            {SECTIONS.map((g) => (
              <div key={g.group} style={{ marginBottom: 8 }}>
                <div
                  style={{
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: ".09em",
                    color: "var(--fg-subtle)",
                    fontWeight: 600,
                    padding: "8px 10px 4px",
                  }}
                >
                  {g.group}
                </div>
                {g.items.map((it) => {
                  const active = section === it.id;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => setSection(it.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "7px 10px",
                        borderRadius: 6,
                        border: "none",
                        background: active ? "var(--bg-selected)" : "transparent",
                        color: active ? "var(--brand-900)" : "var(--fg-muted)",
                        fontSize: 13,
                        fontWeight: active ? 600 : 500,
                        fontFamily: "var(--font-body)",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      <span className="material-icons-outlined" style={{ fontSize: 18 }}>
                        {it.icon}
                      </span>
                      {it.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </Card>

        <div style={{ minWidth: 0 }}>
          {section === "profil" && <ProfilSection user={meQuery.data ?? null} />}
          {section === "vorlagen" && <VorlagenSection />}
          {section === "pdf-vorlagen" && <PdfVorlagenSection isAdmin={isAdmin} />}
          {section === "mail" && <MailSection />}
          {section === "ci-design" && <CiDesignSection isAdmin={isAdmin} />}
          {section === "formulare" && <FormulareSection isAdmin={isAdmin} />}
          {section === "preise" && <PreiseSection isAdmin={isAdmin} />}
          {section === "system" && <SystemSection isAdmin={isAdmin} />}
          {section === "daten" && <DatenSection />}
          {section === "nutzer" && <NutzerSection isAdmin={isAdmin} />}
          {section === "handbuch" && <HandbuchSection />}
          {section === "about" && <AboutSection />}
        </div>
      </div>
    </div>
  );
}
